import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  MapPin,
  ArrowRight,
  Bed,
  Bath,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  X,
  SlidersHorizontal,
} from "lucide-react";
import { fetchHouseLand, fetchSuburbs } from "../../api/public.api.js";
import { Container } from "../../components/layout/Container.jsx";
import { PageHero } from "../../components/shared/PageHero.jsx";
import { SEO } from "../../lib/seo.jsx";
import { useUrlFilters, useUrlPage } from "../../hooks/useUrlFilters.js";
import { formatPrice, formatDirection } from "../../lib/format.js";
import { imageOrPlaceholder, imgErrorFallback } from "../../lib/placeholders.js";
import { cn } from "../../lib/cn.js";

const FILTER_KEYS = ["suburb", "direction", "priceMin", "priceMax", "bedrooms"];

const DIRECTION_OPTIONS = [
  { value: "", label: "Any direction" },
  { value: "north", label: "North" },
  { value: "south_east", label: "South-East" },
  { value: "west", label: "West" },
];

const BEDROOM_OPTIONS = [
  { value: "", label: "Any beds" },
  { value: "3", label: "3 beds" },
  { value: "4", label: "4 beds" },
  { value: "5", label: "5+ beds" },
];

export default function HouseLandPage() {
  const [filters, setFilters, resetFilters] = useUrlFilters({ keys: FILTER_KEYS });
  const [page, setPage] = useUrlPage(1);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    if (page !== 1) setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.suburb, filters.direction, filters.priceMin, filters.priceMax, filters.bedrooms]);

  const suburbsQ = useQuery({
    queryKey: ["public", "house-land", "suburbs"],
    queryFn: fetchSuburbs,
    staleTime: 5 * 60_000,
  });

  const packagesQ = useQuery({
    queryKey: ["public", "house-land", filters, page],
    queryFn: () =>
      fetchHouseLand({
        page,
        limit: 12,
        ...Object.fromEntries(
          Object.entries(filters).filter(([, v]) => v !== "" && v !== undefined)
        ),
      }),
    staleTime: 30_000,
    keepPreviousData: true,
  });

  const packages = packagesQ.data?.items ?? [];
  const meta = packagesQ.data?.meta;
  const setFilter = (patch) => setFilters({ ...filters, ...patch });

  const suburbOptions = [
    { value: "", label: "Any suburb" },
    ...((suburbsQ.data ?? []).map((s) => ({ value: s, label: s }))),
  ];

  return (
    <>
      <SEO
        title="House & Land Packages"
        description="Land + design + finance bundled into a single price across our partner estates in Wollert, Lucas, and Glen Waverley."
      />

      <PageHero
        eyebrow="House & land"
        title="Packages with the price you'll actually pay"
        description="Land, design, council fees, and turn-key inclusions — all priced at contract. No surprises at handover."
      />

      <Container className="py-12 lg:py-16">
        <div className="mb-6 flex items-center justify-between gap-4">
          <p className="text-sm text-text-muted">
            {packagesQ.isLoading
              ? "Loading…"
              : `${meta?.total ?? packages.length} package${
                  meta?.total === 1 ? "" : "s"
                } available`}
          </p>
        </div>

        {/* Top filter bar — same layout as the collections page */}
        <div className="sticky top-16 z-20 -mx-4 mb-8 border-y border-border-subtle bg-bg-base/95 px-4 py-4 backdrop-blur-md lg:top-20 lg:mx-0 lg:rounded-2xl lg:border lg:px-6 lg:py-5">
          <div className="flex flex-wrap items-center gap-3">
            <FilterDropdown
              label="Suburb"
              options={suburbOptions}
              value={filters.suburb ?? ""}
              onChange={(v) => setFilter({ suburb: v })}
            />
            <FilterDropdown
              label="Direction"
              options={DIRECTION_OPTIONS}
              value={filters.direction ?? ""}
              onChange={(v) => setFilter({ direction: v })}
            />
            <FilterDropdown
              label="Bedrooms"
              options={BEDROOM_OPTIONS}
              value={filters.bedrooms ?? ""}
              onChange={(v) => setFilter({ bedrooms: v })}
            />
            <button
              type="button"
              onClick={() => setAdvancedOpen((o) => !o)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-colors",
                advancedOpen || filters.priceMin || filters.priceMax
                  ? "border-accent bg-accent/15 text-accent"
                  : "border-border-subtle bg-bg-elevated text-text-secondary hover:border-border hover:text-text-primary"
              )}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Price range
            </button>

            {hasActive(filters) && (
              <button
                type="button"
                onClick={resetFilters}
                className="ml-auto inline-flex items-center gap-1 text-xs text-text-muted hover:text-accent"
              >
                <RotateCcw className="h-3 w-3" /> Reset
              </button>
            )}
          </div>

          {hasActive(filters) && (
            <div className="mt-3 flex flex-wrap gap-2 border-t border-border-subtle/40 pt-3">
              {filters.suburb && (
                <Chip onRemove={() => setFilter({ suburb: "" })}>{filters.suburb}</Chip>
              )}
              {filters.direction && (
                <Chip onRemove={() => setFilter({ direction: "" })}>
                  {formatDirection(filters.direction)}
                </Chip>
              )}
              {filters.bedrooms && (
                <Chip onRemove={() => setFilter({ bedrooms: "" })}>{filters.bedrooms} beds</Chip>
              )}
              {filters.priceMin && (
                <Chip onRemove={() => setFilter({ priceMin: "" })}>
                  ≥ {formatPrice(Number(filters.priceMin))}
                </Chip>
              )}
              {filters.priceMax && (
                <Chip onRemove={() => setFilter({ priceMax: "" })}>
                  ≤ {formatPrice(Number(filters.priceMax))}
                </Chip>
              )}
            </div>
          )}

          {advancedOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-border-subtle/40 pt-4 sm:grid-cols-2">
              <NumberField
                label="Min price (AUD)"
                value={filters.priceMin ?? ""}
                onChange={(v) => setFilter({ priceMin: v })}
                placeholder="e.g. 600000"
              />
              <NumberField
                label="Max price (AUD)"
                value={filters.priceMax ?? ""}
                onChange={(v) => setFilter({ priceMax: v })}
                placeholder="e.g. 1200000"
              />
            </div>
          )}
        </div>

        {/* Results — full width, 3 per row */}
        {packagesQ.isLoading ? (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:gap-10">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-80 animate-pulse rounded-2xl bg-bg-elevated" />
            ))}
          </div>
        ) : packages.length === 0 ? (
          <EmptyResults onReset={resetFilters} />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:gap-10">
              {packages.map((p) => (
                <PackageCard key={p._id ?? p.slug} pkg={p} />
              ))}
            </div>
            <PaginationBar meta={meta} onChange={setPage} />
          </>
        )}
      </Container>
    </>
  );
}

function FilterDropdown({ label, options, value, onChange }) {
  const active = value !== "";
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        className={cn(
          "appearance-none rounded-full border px-4 py-2 pr-9 text-sm transition-colors focus:outline-none focus:ring-1",
          active
            ? "border-accent bg-accent/15 text-accent focus:ring-accent"
            : "border-border-subtle bg-bg-elevated text-text-secondary hover:border-border hover:text-text-primary focus:ring-accent"
        )}
      >
        {options.map((opt) => (
          <option key={opt.value || "any"} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <ChevronRight
        className={cn(
          "pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 rotate-90",
          active ? "text-accent" : "text-text-muted"
        )}
      />
    </div>
  );
}

function Chip({ children, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent/15 py-1 pl-3 pr-1.5 text-xs text-accent">
      {children}
      <button
        type="button"
        onClick={onRemove}
        className="grid h-5 w-5 place-items-center rounded-full hover:bg-accent/25"
        aria-label="Remove filter"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

function NumberField({ label, value, onChange, placeholder }) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-text-muted">
        {label}
      </p>
      <input
        type="number"
        min="0"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-border bg-bg-elevated-2 px-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </div>
  );
}

function PaginationBar({ meta, onChange }) {
  if (!meta || meta.totalPages <= 1) return null;
  return (
    <nav className="mt-12 flex items-center justify-center gap-2" aria-label="Pagination">
      <button
        type="button"
        disabled={!meta.hasPrev}
        onClick={() => onChange(meta.page - 1)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border-subtle text-text-secondary disabled:opacity-30 hover:border-accent hover:text-accent"
        aria-label="Previous page"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <span className="px-4 py-2 text-sm text-text-secondary">
        Page <span className="text-text-primary">{meta.page}</span> of {meta.totalPages}
      </span>
      <button
        type="button"
        disabled={!meta.hasNext}
        onClick={() => onChange(meta.page + 1)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border-subtle text-text-secondary disabled:opacity-30 hover:border-accent hover:text-accent"
        aria-label="Next page"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </nav>
  );
}

function EmptyResults({ onReset }) {
  return (
    <div className="rounded-2xl border border-dashed border-border-subtle bg-bg-elevated px-6 py-16 text-center">
      <h3 className="font-display text-xl text-text-primary">No packages match these filters</h3>
      <p className="mt-2 text-sm text-text-secondary">
        Try widening the suburb, direction, or price range.
      </p>
      <button type="button" onClick={onReset} className="mt-6 btn-outline">
        Reset filters
      </button>
    </div>
  );
}

function PackageCard({ pkg }) {
  const heroImage =
    pkg.heroImage ||
    pkg.images?.[0] ||
    pkg.linkedDesign?.heroImage ||
    pkg.linkedDesign?.images?.[0];
  const heroSrc = imageOrPlaceholder(heroImage, {
    pool: "houseLand",
    key: pkg.slug ?? pkg._id ?? pkg.estateName,
  });
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated transition-all hover:-translate-y-0.5 hover:border-accent/60 hover:shadow-card-hover">
      <div className="relative aspect-[16/10] overflow-hidden bg-bg-elevated-2">
        <img
          src={heroSrc}
          onError={imgErrorFallback(pkg.slug ?? pkg._id ?? pkg.estateName)}
          alt={pkg.estateName}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute right-4 top-4 rounded-full bg-accent px-3 py-1 text-xs font-semibold text-bg-base shadow-md">
          {formatPrice(pkg.price)}
        </div>
      </div>

      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-display text-xl text-text-primary">{pkg.estateName}</h3>
        <p className="mt-1 inline-flex items-center gap-1 text-sm text-text-muted">
          <MapPin className="h-3.5 w-3.5" />
          {pkg.suburb} · {formatDirection(pkg.direction)}
        </p>

        {pkg.linkedDesign && (
          <div className="mt-4 rounded-md border border-border-subtle bg-bg-elevated-2 px-3 py-2 text-xs text-text-secondary">
            <p className="text-text-primary">{pkg.linkedDesign.name}</p>
            <div className="mt-1 flex items-center gap-3 text-text-muted">
              <span className="inline-flex items-center gap-1">
                <Bed className="h-3 w-3" /> {pkg.linkedDesign.bedrooms}
              </span>
              <span className="inline-flex items-center gap-1">
                <Bath className="h-3 w-3" /> {pkg.linkedDesign.bathrooms}
              </span>
              {pkg.linkedDesign.areaSq && (
                <span className="inline-flex items-center gap-1">
                  <Maximize2 className="h-3 w-3" /> {pkg.linkedDesign.areaSq} sq
                </span>
              )}
            </div>
          </div>
        )}

        {pkg.inclusions?.length > 0 && (
          <ul className="mt-4 list-disc pl-5 text-xs text-text-secondary">
            {pkg.inclusions.slice(0, 3).map((inc, i) => (
              <li key={i}>{inc}</li>
            ))}
          </ul>
        )}

        <div className="mt-auto flex items-center justify-between gap-3 pt-6">
          <span className="text-xs text-text-muted">{pkg.lotSize}</span>
          <Link
            to="/contact-us/new-build"
            className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent-hover"
          >
            Enquire
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </article>
  );
}

function hasActive(f) {
  return Boolean(f.suburb || f.direction || f.bedrooms || f.priceMin || f.priceMax);
}